<template>

  <div>
    <div class="ob-row">
      <div class="ob-col">
        <h2>{{ $t('interventions.date-and-time') }}</h2>
        <duration-picker
          :dtimes="durationTimes"
          @changed="durationTimeChanged($event)"
          data-test="intervention-duration"
        ></duration-picker>
      </div>

      <div class="ob-col">
        <h2>{{ $tc('users.entity-name', 2) }}</h2>
        <users-in
          v-if="intervention.usersInInterventions"
          :items="intervention.usersInInterventions"
          @change="addItem($event, 'users-in-interventions')"
          @remove="removeItem($event, 'users-in-interventions')"
          data-test="users-in"
        ></users-in>
      </div>

      <div class="ob-col" v-if="$canm('mnt', 'useChecklists')">
        <h2>{{ $tc('checklists.entity-name', 2) }}</h2>
        <checklists-in
          v-if="intervention.checklistsInInterventions"
          :items="intervention.checklistsInInterventions"
          :options="checklists"
          @change="addItem($event, 'checklists-in-interventions')"
          @remove="removeItem($event, 'checklists-in-interventions')"
          @click="$router.push({ name: `checklists-in-interventions/id`, params: { id: $event } })"
        ></checklists-in>
      </div>

      <div class="ob-col" v-if="$canm('mnt', 'useProducts')">
        <h2>{{ $tc('products.entity-name', 2) }}</h2>
        <products-in
          v-if="intervention.productsInInterventions"
          :items="intervention.productsInInterventions"
          @change="addItem($event, 'products-in-interventions')"
          @remove="removeItem($event, 'products-in-interventions')"
        ></products-in>
      </div>
    </div>

    <div class="ob-row mt-0">
      <div class="ob-col solution">
        <h2>{{ $t('solution') }}</h2>
        <v-textarea
          auto-grow
          :rows="5"
          v-model="intervention.description"
          @change="refresh()"
          data-test="solution"
        ></v-textarea>
      </div>

      <div class="ob-col">
        <intervention-files
          :intervention="intervention"
          @change="refresh()"
          @remove="removeItem($event, 'files-in-interventions')"
        ></intervention-files>
      </div>
    </div>

    <div class="ob-row mt-0 no-box" v-if="closable">
      <div class="ob-col text-center">
        <v-btn text color="primary" @click="closeIssue()">{{ $t('interventions.solve-issue') }}</v-btn>
      </div>
    </div>
  </div>

</template>

<script>
  import DurationPicker from './duration-picker'
  import UsersIn from '../../users/components/users-in'
  import ChecklistsIn from '../../checklists/components/checklists-in'
  import ProductsIn from '../../products/components/products-in'
  import InterventionFiles from './intervention-files'

  export default {
    name: 'intervention-form',

    props: [ 'intervention', 'issue' ],

    computed: {
      durationTimes () {
        return this.$store.state.site.modules.mnt.interventionTimes.map(time => this.intervention[time])
      },

      checklists () {
        if (!this.issue)
          return []

        const checklistsInAssetTypes = this.issue.asset.assetTypeAndAllHisParents.checklistsInAssetTypes
        if (checklistsInAssetTypes) {
          const myChecklistsIds = checklistsInAssetTypes
            .map(checklistIn => checklistIn.checklistId)
          const myChecklists = (this.$store.state.items['checklists'] || [])
            .filter(checklist => myChecklistsIds.includes(checklist.id))
          const myUsedChecklistsIds = (this.intervention.checklistsInInterventions || [])
            .map(checklistIn => checklistIn.checklistId)
          return myChecklists
            .filter(checklist => !myUsedChecklistsIds.includes(checklist.id))
            .map(chl => ({ id: chl.id, name: chl.name + ' v.' + chl.version }))
        }

        return []
      },

      closable () {
        return !this.durationTimes.map(time => !!time ? time : null).includes(null)
      }
    },

    methods: {
      durationTimeChanged (dates) {
        this.$store.state.site.modules.mnt.interventionTimes.forEach((time, index) => this.intervention[time] = dates[index])
        this.refresh()
      },

      async addItem (object, model) {
        await this.$store.dispatch('updateItem', {
          modelName: model,
          item: Object.assign({ interventionId: this.intervention.id }, object)
        })

        this.refresh()
      },

      async removeItem (id, model) {
        await this.$store.dispatch('destroyItem', {
          model: { name: model },
          id: id
        })

        this.refresh()
      },

      async closeIssue () {
        await this.$store.dispatch('updateItem', {
          modelName: 'issues',
          item: Object.assign(this.issue, { closed: true })
        })

        this.$router.push({ name: 'assets/id', params: { id: this.issue.assetId } })
      },

      refresh () {
        this.$emit('change', this.intervention)
      }
    },

    components: {
      InterventionFiles,
      ProductsIn,
      UsersIn,
      ChecklistsIn,
      DurationPicker
    }
  }
</script>

<style scoped lang="scss">

  @import '../../../assets/variables';
  @import '../../../assets/responsive';

  ::v-deep {
    .v-list:not(.v-select-list) > .v-list-item {
      padding: 0;
    }
  }

  h2 {
    font-weight: 300;
  }

  .ob-col:not(.no-box),
  .ob-col-3,
  .ob-col-6 {
    padding: $spacing-default;
    border: 1px solid $color-gray;
    background-color: white;
    border-radius: $border-radius;
  }

  @include media('<tablet') {
    .solution {
      grid-row: 2;
    }
  }

</style>
